import classNames from 'classnames';

import { Typography } from '../Typography';
import { Box } from '../Box';
import { StagesSize } from '../Stages';

import { StageEnd } from './StageEnd';
import { StageStart } from './StageStart';
import { useStyles } from './Stage.styles';

export type StageStatus = 'initial' | 'active' | 'completed';

/**
 * Свойства компонента {@link Stage}
 */
interface StageProps {
  /**
   * Дополнительное имя CSS-класса для применения к Stage.
   */
  className?: string;

  /**
   * Определяет, является ли этот этап первым в процессе.
   *
   * @defaultValue `false`
   */
  first?: boolean;

  /**
   * Метка для отображения этого этапа.
   */
  label: string;

  /**
   * Определяет, является ли этот этап последним в процессе.
   *
   * @defaultValue `false`
   */
  last?: boolean;

  /**
   * Размер этапа, влияющий на его внешний вид.
   *
   * @defaultValue `'medium'`
   */
  size?: StagesSize;

  /**
   * Статус этапа ('initial', 'active' или 'completed').
   *
   * @defaultValue `'initial'`
   */
  status?: StageStatus;

  /**
   * Определяет, должен ли этап занимать всю ширину своего контейнера.
   *
   * @defaultValue `false`
   */
  fullWidth?: boolean;
}

/**
 * Компонент, представляющий этап в процессе этапов, который может находиться в статусе 'initial', 'active' или 'completed'.
 * Включает индикаторы начала и конца, метку и может быть стилизован на основе своей позиции и размера.
 *
 * @public
 * @param className - Дополнительное имя CSS-класса для применения к Stage.
 * @param first - Определяет, является ли этот этап первым в процессе.
 * @param label - Метка для отображения этого этапа.
 * @param last - Определяет, является ли этот этап последним в процессе.
 * @param size - Размер этапа, влияющий на его внешний вид.
 * @param status - Статус этапа ('initial', 'active' или 'completed').
 * @param fullWidth - Определяет, должен ли этап занимать всю ширину своего контейнера.
 * @returns Rомпонент Stage.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link StageProps}
 */
export const Stage = ({
  className,
  first = false,
  label,
  last = false,
  fullWidth = false,
  size = 'medium',
  status = 'initial',
}: StageProps) => {
  const { classes } = useStyles();

  const completed = status === 'completed';
  const active = status === 'active';

  const widthForContainer = fullWidth ? '100%' : 'auto';

  return (
    <Box
      display="flex"
      flexWrap="nowrap"
      width={widthForContainer}
      className={classNames(
        className,
        !first && classes.offsetToLeft,
        !first && size === 'small' && classes.offsetToLeftSmall,
        'aqa_stage',
        `aqa_stage_${status}`,
      )}
      testId="stage-test-id"
    >
      <Box display="flex" width={widthForContainer}>
        <StageStart first={first} status={status} />
        <Box
          opacity={active ? 0.6 : 1}
          className={classNames(
            classes.content,
            fullWidth && classes.fullWidthStageContent,
            completed && classes.completed,
            active && classes.active,
            size === 'medium' && classes.contentMedium,
          )}
        >
          <Typography
            variant="caption"
            color={
              completed || active ? 'text.dark.primary' : 'text.light.primary'
            }
          >
            {label}
          </Typography>
        </Box>
        <StageEnd last={last} status={status} />
      </Box>
    </Box>
  );
};
